import { Button } from "@bcc/components/ui/button";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { setProductImageLightboxTestId } from "./product-image-lightbox.utils";

interface ProductImageLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  productName: string;
  variantImages?: string[];
  testId?: string;
}

export function ProductImageLightbox({
  isOpen,
  onClose,
  imageUrl,
  productName,
  variantImages = [],
  testId,
}: ProductImageLightboxProps) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = [imageUrl, ...variantImages];
  const hasMultipleImages = images.length > 1;

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    const handleArrowKeys = (e: KeyboardEvent) => {
      if (!hasMultipleImages) return;

      if (e.key === "ArrowLeft") {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
      } else if (e.key === "ArrowRight") {
        setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("keydown", handleArrowKeys);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("keydown", handleArrowKeys);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose, hasMultipleImages, images.length]);

  if (!isOpen) return null;

  return (
    <div
      data-testid={testId ?? setProductImageLightboxTestId("container")}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t("products.imageLightbox.imagePreview")}
    >
      <div
        className="relative max-w-3xl w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image container */}
        <div className="relative bg-surface-1 rounded-xl shadow-2xl p-4">
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 text-foreground hover:bg-muted z-10"
            onClick={onClose}
            aria-label={t("products.imageLightbox.closePreview")}
          >
            <X className="h-5 w-5" />
          </Button>
          <div className="relative aspect-square bg-muted rounded-lg overflow-hidden">
            <img
              src={images[currentIndex] || "/placeholder.svg"}
              alt={`${productName} - Image ${currentIndex + 1}`}
              className="w-full h-full object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "/rustic-coffee-bag.png";
              }}
            />
          </div>

          {/* Image info footer */}
          <div className="mt-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{productName}</p>
              {hasMultipleImages && (
                <p className="text-xs text-muted-foreground">
                  {t("products.imageLightbox.imageCounter", {
                    current: currentIndex + 1,
                    total: images.length,
                  })}
                </p>
              )}
            </div>

            {/* Navigation arrows */}
            {hasMultipleImages && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() =>
                    setCurrentIndex((prev) =>
                      prev > 0 ? prev - 1 : images.length - 1,
                    )
                  }
                  aria-label={t("products.imageLightbox.previousImage")}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() =>
                    setCurrentIndex((prev) =>
                      prev < images.length - 1 ? prev + 1 : 0,
                    )
                  }
                  aria-label={t("products.imageLightbox.nextImage")}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
